//  ----------------------------------------------------------------------------
//  Dependencies
//  ----------------------------------------------------------------------------
import {Snackbar , Alert} from '@mui/material';
//  -- Components ---------------------
import { Col, Row , Dropdown } from 'antd';
import AssessmentCoversCard from 'components/AssessmentCover';
import Section from 'components/Section';
import React, { useEffect  , useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import styled from 'styled-components';
import { useLocation, useNavigate } from "react-router-dom";
import { find, map , get , groupBy  } from "lodash"
import { useCurrentOrgPermission, useOrgTimezone } from 'store/organizations/selectors';
import { useAlertView } from "store/alert/selectors";
//  -- Constants ----------------------
import { useExams } from 'store/exams/selectors';
import COLOR from 'utils/constants/color';
import { useDemoStatus } from "store/users/selectors";
import Loading from "components/Loading";
import { handleListExams } from 'store/exams/thunks';
import { isAfterTime, isBeforTime } from 'utils/methods/math';
import dayjs from 'utils/dayjsConfig';
import { CaretDownOutlined } from '@ant-design/icons';
import { Button } from '@mui/material'
import { BackButtonEbook } from 'components/UI/buttons/Backbutton';
import SentryWrap from 'components/Sentry';
import { redirectToHome } from 'utils/config/navigation';
import { useCurrentClass } from 'store/classes/selectors';
import { useUserClasses } from 'store/userClasses/selectors';
import { handleListUserClasses, handleUpdateClassConfigMeta } from 'store/userClasses/thunks';
import { DeleteModal } from 'components/Dialog';
//  -- Features -----------------------

//  ----------------------------------------------------------------------------
//  Styles
//  ----------------------------------------------------------------------------

export const Wrapper = styled.div`
    height: 100%;
    width: 100%;
`;

const ListItem = styled.li`
  margin: 0px 4% 0px;
`
const List = styled.ul`
    height: auto;
    align-items: flex-start;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    list-style-type: none;
    justify-content:center;
`;

const PaddedBtn = styled.button`
    background-color: transparent;
    border: none;
    cursor: pointer;
    font-size: 12px;
    font-weight: 600;
    outline: none;
    padding: 8px;
    text-align: left;
    width: 100%;
`;

const HideShowSectionBtn = styled.button`
    margin-left: 8px;
    font-size: 15px;
    padding: 10px 22px;
    cursor: ${p => p.$disabled ? 'not-allowed' : 'pointer'};
    opacity: ${p => p.$disabled ? 0.6 : 1};
    border: none;
    border-radius: 10px;
    background: linear-gradient(135deg, #5b7cba 0%, #4a6fa5 100%);
    color: #fff;
    font-weight: 600;
    box-shadow: 0 3px 10px rgba(74, 111, 165, 0.4);
    transition: background 0.2s, box-shadow 0.2s, transform 0.15s;
    &:hover:not(:disabled) {
        background: linear-gradient(135deg, #6b8ccc 0%, #5b7cba 100%);
        box-shadow: 0 4px 14px rgba(74, 111, 165, 0.5);
        transform: translateY(-1px);
    }
    &:active:not(:disabled) {
        transform: translateY(0);
    }
`;

const ShowSectionLink = styled.button`
    margin-left: auto;
    font-size: 15px;
    padding: 10px 22px;
    cursor: pointer;
    border: none;
    border-radius: 10px;
    background: linear-gradient(135deg, #3d9d7a 0%, #2d8a6e 100%);
    color: #fff;
    font-weight: 600;
    box-shadow: 0 3px 10px rgba(45, 138, 110, 0.4);
    transition: background 0.2s, box-shadow 0.2s, transform 0.15s;
    &:hover {
        background: linear-gradient(135deg, #4dad8a 0%, #3d9d7a 100%);
        box-shadow: 0 4px 14px rgba(45, 138, 110, 0.5);
        transform: translateY(-1px);
    }
    &:active {
        transform: translateY(0);
    }
`;

//  ----------------------------------------------------------------------------
//  Container
//  ----------------------------------------------------------------------------

const noAssignedMsg = "Not Assigned by Teacher"

const AssessmentContainer = ({
    currentUser,
    classActivities,
    classQuizzes
}) => {
  	const navigate = useNavigate();
    const exams = useExams()
    const orgTimezone = useOrgTimezone()
    const currentClass = useCurrentClass();
    const userClasses = useUserClasses();
    const dispatch = useDispatch()
    const [loading, setLoading] = useState(false);
    const [state, setState] = React.useState({
        open: false,
        message: ""
    });
    const demoStatus = useDemoStatus()
    const isReviewAccount = currentUser?.reviewAccount || false
    const isStudent = currentUser?.role === "student" || false
    const [, invokeAlert, revokeALert] = useAlertView()
    const orgPermissions = useCurrentOrgPermission()
    const [activeSessionHighSchool, setActiveSessionHighSchool] = useState(null);
    const [activeSessionMiddleSchool, setActiveSessionMiddleSchool] = useState(null);
    const [examGroupData,setExamGroupData] = useState({})
    const [hideLoader, setHideLoader] = useState(false);
    const [hideConfirmationModal, setHideConfirmationModal] = useState(false);
    const [pendingHideSection, setPendingHideSection] = useState(null);
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [pendingShowSection, setPendingShowSection] = useState(null);
    const [hiddenSections, setHiddenSections] = useState({ highSchool: false, middleSchool: false })

    useEffect(() => {
        dispatch(handleListUserClasses(currentUser.id))
    }, [])
    
    useEffect(() => {
        const classInfo = find(userClasses, ['id', currentClass?.id]);
        if (classInfo?.config && classInfo.config.meta_data) {
            const metaData = classInfo.config.meta_data;
            setHiddenSections({ highSchool: metaData.highSchool, middleSchool: metaData.middleSchool })
        }
    }, [userClasses, currentClass, exams])

    useEffect(() => {
        if (!hideConfirmationModal) {
            setPendingHideSection(null);
        }
    }, [hideConfirmationModal]);

    useEffect(() => {
        if (!showConfirmationModal) {
            setPendingShowSection(null);
        }
    }, [showConfirmationModal]);

    useEffect(() => {
        if (orgPermissions && typeof orgPermissions['exams'] === "boolean") {
            if (!orgPermissions['exams']) {
                invokeAlert()
                setTimeout(() => {
                    navigate(redirectToHome(currentUser) || '-1');
                    revokeALert()
                }, 5000)
            }
        }
    }, [orgPermissions, invokeAlert, revokeALert, navigate])

    useEffect(() => {
        if (exams) {
            const sortedExams = exams.sort((a, b) => {
                if (a.order > b.order) {
                    return 1
                }
                if (a.order < b.order) {
                    return -1
                }
                return 0
            })
            const years = new Date().getFullYear();
            const grp = groupBy(sortedExams,"session")
            const upcomming_year = `${years}-${years+1}`;
            const downcomming_year = `${years-1}-${years}`;
            const pickDefaultSessionKey = () => {
                if (grp[upcomming_year]) return upcomming_year;
                if (grp[downcomming_year]) return downcomming_year;
                const keys = Object.keys(grp);
                return keys[0] || null;
            };
            setExamGroupData(grp);
            const defaultKey = pickDefaultSessionKey();
            setActiveSessionHighSchool((prev) => (prev && grp[prev] ? prev : defaultKey));
            setActiveSessionMiddleSchool((prev) => (prev && grp[prev] ? prev : defaultKey));
        }

    }, [exams])

    const loadExams = async () => {
        setLoading(get(exams, "length") == 0)
        await dispatch(handleListExams())
        setLoading(false)
    }
    useEffect(() => {
        loadExams()
    }, [])
    const handleClose = () => {
        setState({ open: false, message: "" });
    };
    const showMsg = (text) => {
        setState({ open: true, message: text });
    };
    const showCopyRight = () => {
        setState({ open: true, message: "Restricted access (legend)" });
    }
    const onCoverCLick = (obj) => {
        const chapter = get(obj,"bookId")

        if (demoStatus || isReviewAccount) {
            showCopyRight()
            return;
        }
        const url = `/ebook?chapter=${chapter}&page=1&activity=null&quiz=null`
        const activitiy = find(classActivities, k => get(k, "data.bookId") == chapter)
        const quiz = find(classQuizzes, k => get(k, "data.bookId") == chapter)
        if(isStudent){
            if (!activitiy && !quiz) {
                showMsg(noAssignedMsg)
                return;
            }else{
                navigate(url); 
            }
        }else{
            const startDate =  get(obj,"startDate")
            const endDate = get(obj,"endDate")
            if (startDate && endDate) {
                if (isAfterTime(startDate, dayjs(), orgTimezone)) {
                    showMsg(` Available by ${dayjs(startDate).format("MM/DD/YYYY hh:mm A")}`)
                } else if (isBeforTime(endDate, dayjs(), orgTimezone)) {
                    showMsg(` Exam is ended on ${dayjs(endDate).format("MM/DD/YYYY hh:mm A")}`)
                } else {
                    navigate(url);
                }

            } else if (startDate) {
                if (isAfterTime(startDate, dayjs(), orgTimezone)) {
                    showMsg(` Available by ${dayjs(startDate).format("MM/DD/YYYY hh:mm A")}`)
                } else {
                    navigate(url);
                }

            } else if (endDate) {
                if (isBeforTime(endDate, dayjs(), orgTimezone)) {
                    showMsg(` Exam is ended on ${dayjs(endDate).format("MM/DD/YYYY hh:mm A")}`)
                } else {
                    navigate(url);
                }
            }
            else {
                navigate(url);
            }
        }
    }
    const sortExam = () => {
        if (exams) {
           return exams.sort((a, b) => {
                if (a.order > b.order) {
                    return 1
                }
                if (a.order < b.order) {
                    return -1
                }
                return 0
            })
        } else {
            return [];
        }
    }

    const onSelectSessionHighSchool = (key) => {
        setActiveSessionHighSchool(key);
    };

    const onSelectSessionMiddleSchool = (key) => {
        setActiveSessionMiddleSchool(key);
    };

    const canHideSection = (section) => {
        if (section === 'highSchool') return !hiddenSections.middleSchool
        if (section === 'middleSchool') return !hiddenSections.highSchool
        return false
    }

    const toggleSection = async (section) => {
        const config = {
            classId: currentClass?.id,
            meta_data: {
                [section]: !hiddenSections[section]
            }
        }
        setHideLoader(true);
        await dispatch(handleUpdateClassConfigMeta(config));
        dispatch(handleListUserClasses(currentUser.id))
        setHideLoader(false);
        setHiddenSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }))
    }

    const confirmHideSection = async () => {
        if (pendingHideSection) {
            await toggleSection(pendingHideSection);
        }
        setPendingHideSection(null);
        setHideConfirmationModal(false);
    };

    const confirmShowSection = async () => {
        if (pendingShowSection) {
            await toggleSection(pendingShowSection);
        }
        setPendingShowSection(null);
        setShowConfirmationModal(false);
    };

    const buildSessionMenu = (onSelect) => {
       const sortedYears = Object.keys(examGroupData).sort((a, b) => {
        const yearA = parseInt(a.split('-')[0], 10);
        const yearB = parseInt(b.split('-')[0], 10);
        return yearB - yearA;
        });

        return {
            items: map(sortedYears, x => ({
                key: x,
                label: (
                    <PaddedBtn onClick={() => onSelect(x)}>{x}</PaddedBtn>
                ),
                style: { padding: '0px' }
            }))
        }
    }

    const { open, message } = state;


    const highLevel = (activeSessionHighSchool && examGroupData[activeSessionHighSchool]?.[0]) || {};
    const middleLevel = (activeSessionMiddleSchool && examGroupData[activeSessionMiddleSchool]?.[1]) || {};
    const singleExamSectionVisible = Boolean(hiddenSections.highSchool) !== Boolean(hiddenSections.middleSchool);
    
    return (
    <SentryWrap>
        <Wrapper
            className={`examWrapperCstm${singleExamSectionVisible ? ' exam-layout-single' : ''}`}
            onContextMenu={(e) => {
                e.preventDefault();
                showCopyRight()
            }}>
            <DeleteModal
              message={`You have assigned from this exam. Are you sure you want to hide it?`}
              deleteToggle={hideConfirmationModal}
              setDeleteToggle={setHideConfirmationModal}
              loading={hideLoader}
              onDelete={confirmHideSection}
              buttonTitle="Yes"
              onClickCancel={() => setPendingHideSection(null)}
            />
            <DeleteModal
              message={`Are you sure you want to show this exam?`}
              deleteToggle={showConfirmationModal}
              setDeleteToggle={setShowConfirmationModal}
              loading={hideLoader}
              onDelete={confirmShowSection}
              buttonTitle="Yes"
              onClickCancel={() => setPendingShowSection(null)}
            />
            {loading ? (
                <Loading />
            ) : (<>
                <Snackbar
                    anchorOrigin={{ vertical: "top", horizontal: "center" }}
                    open={open}
                    autoHideDuration={3000}
                    onClose={handleClose}>
                    <Alert onClose={handleClose} severity="error">
                        <strong>ALERT:</strong> {message}
                    </Alert>
                </Snackbar>

            <div className='exmSpacing'>
              {currentUser.role=='admin' ? <BackButtonEbook onClick={()=> navigate(-1)} style={{ borderTopLeftRadius: 0, borderTopRightRadius: 0, minWidth: 100 }}/>:null}
                   
                     <div className='examTopCstm'>
                                           
                        <Row className='examTopRowCstm'>
                         {!hiddenSections.highSchool && (
                         <Col className='examTopColLft' xs={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }} xl={{ span: hiddenSections.middleSchool ? 24 : 12 }}>
                            <div className='highSchoolUpr' style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                              <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                                <p style={{ margin: 0 }}>High School Exams</p>
                                <Dropdown menu={buildSessionMenu(onSelectSessionHighSchool)} placement="bottomLeft" trigger={['click']} className='examDateDropDown'>
                                  <Button onClick={e => e.preventDefault()}>
                                    {activeSessionHighSchool ? activeSessionHighSchool : 'sessions'}
                                    <CaretDownOutlined className='highSchoolDrpIcon' />
                                  </Button>
                                </Dropdown>
                              </div>
                              {currentUser.role !== 'student' && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  {hiddenSections.middleSchool ? (
                                    <ShowSectionLink
                                      onClick={() => {
                                        setPendingShowSection('middleSchool');
                                        setShowConfirmationModal(true);
                                      }}
                                    >
                                        Show Middle School Exams
                                    </ShowSectionLink>
                                  ) : (
                                    <HideShowSectionBtn
                                      $disabled={!canHideSection('highSchool')}
                                      onClick={() => {
                                        if (!canHideSection('highSchool')) return;
                                        setPendingHideSection('highSchool');
                                        setHideConfirmationModal(true);
                                      }}
                                    >
                                        Hide Exams
                                    </HideShowSectionBtn>
                                  )}
                                </div>
                              )}
                            </div> 
                            <Section>
                              <List className='examUlList'>
                                {map(highLevel?.exams, (obj, i) => (
                                  <Col xs={{ span: 8 }} md={{ span: 8 }} lg={{ span: 8 }} key={obj.id}>
                                    <ListItem>
                                        <AssessmentCoversCard 
                                            data={obj} 
                                            handleClick={() => onCoverCLick(obj)} 
                                            labelBgColor={COLOR.redishpink} 
                                            label={get(highLevel, "displayName")} />
                                    </ListItem>
                                  </Col>
                                ))}
                              </List>
                            </Section>
                         </Col>
                         )}
                         {!hiddenSections.middleSchool && (
                         <Col className='examTopColRght' xs={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }} xl={{ span: hiddenSections.highSchool ? 24 : 12 }}>
                            <div className='highSchoolUpr highSchoolBotomUpr text-start' style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                             <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                               <p style={{ margin: 0 }}>Middle School Exams</p>
                               <Dropdown menu={buildSessionMenu(onSelectSessionMiddleSchool)} placement="bottomLeft" trigger={['click']} className='examDateDropDown'>
                                 <Button onClick={e => e.preventDefault()}>
                                   {activeSessionMiddleSchool ? activeSessionMiddleSchool : 'sessions'}
                                   <CaretDownOutlined className='highSchoolDrpIcon' />
                                 </Button>
                               </Dropdown>
                             </div>
                             {currentUser.role !== 'student' && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  {hiddenSections.highSchool ? (
                                    <ShowSectionLink
                                      onClick={() => {
                                        setPendingShowSection('highSchool');
                                        setShowConfirmationModal(true);
                                      }}
                                    >
                                        Show High School Exams
                                    </ShowSectionLink>
                                  ) : (
                                    <HideShowSectionBtn
                                      $disabled={!canHideSection('middleSchool')}
                                      onClick={() => {
                                        if (!canHideSection('middleSchool')) return;
                                        setPendingHideSection('middleSchool');
                                        setHideConfirmationModal(true);
                                      }}
                                    >
                                        Hide Exams
                                    </HideShowSectionBtn>
                                  )}
                                </div>
                             )}
                            </div> 
                            <Section>
                              <List className='examUlList'>
                               {map(middleLevel?.exams, (obj, i) => (
                                 <Col xs={{ span: 8 }} md={{ span: 8 }} lg={{ span: 8 }} key={obj.id}>
                                    <ListItem>
                                        <AssessmentCoversCard 
                                            data={obj} 
                                            handleClick={() => onCoverCLick(obj)} 
                                            labelBgColor={COLOR.redishpink} 
                                            label={get(middleLevel, "displayName")} 
                                        />
                                    </ListItem>
                                  </Col>
                                ))}
                              </List>
                            </Section>
                         </Col>
                         )}
                      </Row>

                   </div>

                </div>

            </>)}

        </Wrapper>
    </SentryWrap>
    );
};


//  ----------------------------------------------------------------------------
//  Redux
//  ----------------------------------------------------------------------------
const mapStateToProps = state => ({
    currentUser: state.currentUser,
    classActivities: state.classActivities,
    classQuizzes: state.classQuizzes,
});

export default connect(mapStateToProps, null)(AssessmentContainer);
